import { datadogRum } from '@datadog/browser-rum'
import React from 'react'
import ErrorPage, { ErrorType } from '../ErrorPage'

interface Props {
  children: React.ReactNode
  /**
   * When this value changes, the boundary clears its error state and retries
   * rendering its children. Pass the current route so navigating away from a
   * crashed page recovers instead of trapping the user on the error page.
   */
  resetKey?: unknown
}

interface State {
  hasError: boolean
}

/**
 * Catches render-phase errors in the subtree and shows the generic error page
 * instead of white-screening the whole app. Errors are reported to Datadog RUM.
 */
class ErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false }

  static getDerivedStateFromError(): State {
    return { hasError: true }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    datadogRum.addError(error, {
      componentStack: errorInfo.componentStack
    })
  }

  componentDidUpdate(prevProps: Props): void {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false })
    }
  }

  resetError = (): void => {
    this.setState({ hasError: false })
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      return (
        <ErrorPage
          errorType={ErrorType.GenericError}
          onReset={this.resetError}
        />
      )
    }
    return this.props.children
  }
}

export default ErrorBoundary
